/**
 * CMS-neutral link primitive: the one shape both adapters hand every component a link in.
 *
 * Each adapter converts its CMS's own link (a Sitecore link value, a Sanity `generalLink`) into this
 * shape once, wherever the link sits in a component's content, as it does rich text into HTML.
 * `href` is the whole URL, query string and anchor included, so nothing downstream adds them again.
 * A link whose URL cannot be built keeps an empty `href`. `text` and `title` are the link's own: no
 * adapter borrows a label from anywhere else. An empty one means none; Sanity leaves it out, Sitecore
 * sends it as it stores it.
 */
export interface Link {
  href: string;
  text?: string;
  target?: string;
  /** Set with `target: '_blank'`, so a link that opens a new tab never hands it this page. */
  rel?: string;
  /** Optional discriminator: 'internal' | 'external' | 'anchor' | 'mailto' | 'tel'. */
  linkType?: string;
  /** The tooltip, which screen readers also read out. */
  title?: string;
}

/**
 * The link, or undefined when it leads nowhere.
 *
 * A component reads a link from its content as it comes, already converted, and shows it only when it
 * has a URL: a link whose URL could not be built keeps an empty `href`. This is that check and nothing
 * else. It converts nothing, so it takes any value with an optional `href`, including a link field that
 * a content type still types with the CMS's own link shape.
 */
export const linkWithUrl = (link: Partial<Link> | null | undefined): Link | undefined =>
  link?.href ? (link as Link) : undefined;
